import React, { useEffect, useState, useCallback, useMemo } from "react";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Divider,
  Grid,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Card from "@mui/material/Card";
import { useNavigate, useLocation } from "react-router-dom";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import axios from "axios";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import HOST_URL from "../../../utils/Url";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import details from "../../../assets/images/face-recognition.png";
import SwipeableTemporaryDrawer from "./SwipeableTemporaryDrawer";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
} from "@mui/x-data-grid";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp";
import { usePermissionData } from "../../../hooks/usePermissionData";
import dayjs from "dayjs";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "180vh",
  height: "85vh",
  bgcolor: "#f5f5f5",
  border: "1px solid #000",
  boxShadow: 24,
  p: 4,
  borderRadius: 10,
};

interface UserData {
  useR_ID: string;
  ranK_ID: number;
  suR_NAME: string;
  firsT_NAME: string;
  middlE_NAME: string;
  useR_CODE: string;
  dob: string;
  gendeR_ID: number;
  cuR_PHONE: string;
  cuR_MOBILE: string;
  email: string;
  iS_ACTIVE: boolean;
  useR_TYPE_ID: number;
  logiN_NAME: string;
  password: string;
  rolename: string;
  id: string;
  serialNo: number;
  name: string;
}

export default function UserManagement() {
  const [rows, setRows] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [drawerOpenUser, setDrawerOpenUser] = useState(false);
  const [drawerData, setDrawerData] = useState<UserData[]>([]);
  const [pdfReport, setPdfReport] = useState<string | null>(null);
  const [isReportLoading, setIsReportLoading] = useState(true);
  const [docOpens, setDocOpens] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [columns, setColumns] = useState<GridColDef[]>([]);

  const { permissionData, loading: permissionLoading } = usePermissionData();

  // Handlers for modal
  const handleOtherDocClose = useCallback(() => {
    setDocOpens(false);
  }, []);

  const handleOtherDocOpen = useCallback(() => {
    setDocOpens(true);
  }, []);

  // Memoized fetch function
  const getList = useCallback(async () => {
    try {
      setIsLoading(true);
      const collectData = {
        useR_ID: "-1",
      };

      const response = await axios.post(
        HOST_URL.HOST_URL + `USER/GetUSER`,
        collectData
      );

      if (response.data.data) {
        const arr = response.data.data.map((item: any, index: number) => ({
          ...item,
          serialNo: index + 1,
          id: item.useR_ID,
          name: `${item.firsT_NAME} ${item.middlE_NAME === " " ? item.middlE_NAME + " " : ""}${item.suR_NAME}`,
          dob: dayjs(item.dob).format("YYYY-MM-DD"),
        }));
        setRows(arr);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load users");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    getList();
  }, [getList]);

  // Setup columns based on permission data
  useEffect(() => {
    if (permissionLoading || rows.length === 0) return;

    const baseColumns: GridColDef[] = [
      {
        field: "serialNo",
        headerName: t("text.SrNo"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 60
      },
      {
        field: "name",
        headerName: t("text.Name"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 150
      },
      {
        field: "logiN_NAME",
        headerName: t("text.LoginName"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 150
      },
      {
        field: "email",
        headerName: t("text.Email"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 200
      },
      {
        field: "dob",
        headerName: t("text.DOB"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 120
      },
      {
        field: "rolename",
        headerName: t("text.Role"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        minWidth: 120
      },
      {
        field: "isActive",
        headerName: t("text.Status"),
        flex: 1,
        headerClassName: "MuiDataGrid-colCell",
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            {params.row.iS_ACTIVE === true ? (
              <Chip
                label={t("text.Active")}
                color="success"
                style={{ fontSize: "14px" }}
              />
            ) : (
              <Chip
                label={t("text.InActive")}
                color="error"
                style={{ fontSize: "14px" }}
              />
            )}
          </Stack>
        ),
        minWidth: 100
      },
    ];

    // Add actions column only if user has edit, delete, or view permissions
    if (permissionData?.isEdit || permissionData?.isDel || permissionData?.isView) {
      const actionsColumn: GridColDef = {
        field: "actions",
        headerClassName: "MuiDataGrid-colCell",
        headerName: t("text.Action"),
        width: 200,
        renderCell: (params) => (
          <Stack
            spacing={1}
            direction="row"
            sx={{ alignItems: "center", marginTop: "5px" }}
          >
            {permissionData?.isEdit && (
              <EditIcon
                style={{
                  fontSize: "20px",
                  color: "blue",
                  cursor: "pointer",
                }}
                className="cursor-pointer"
                onClick={() => routeChangeEdit(params.row)}
              />
            )}

            {/* Details icon - shows if user has any permission to view this page */}
            <img
              src={details}
              width={20}
              height={20}
              style={{
                cursor: "pointer",
              }}
              onClick={() => {
                getDrawerData(params.row);
              }}
              alt="View Details"
            />

            {permissionData?.isDel && (
              <DeleteIcon
                style={{
                  fontSize: "20px",
                  color: "red",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setDeleteId(params.row.id);
                  confirmDialog({
                    message: "Do you want to delete this record?",
                    header: "Delete Confirmation",
                    icon: "pi pi-info-circle",
                    acceptClassName: "p-button-danger",
                    accept: () => handleDeleteAccept(params.row.id),
                    reject: handleDeleteReject,
                  });
                }}
              />
            )}

            {permissionData?.isEdit && (
              <Switch
                checked={Boolean(params.row.iS_ACTIVE)}
                style={{
                  color: params.row.iS_ACTIVE ? "green" : "#FE0000",
                }}
                onChange={(event) => handleSwitchChange(event, params.row)}
                inputProps={{
                  "aria-label": "Toggle Switch",
                }}
              />
            )}
          </Stack>
        ),
      };

      setColumns([actionsColumn, ...baseColumns]);
    } else {
      // If no permissions, show minimal actions (just details)
      const viewOnlyColumn: GridColDef = {
        field: "actions",
        headerClassName: "MuiDataGrid-colCell",
        headerName: t("text.Action"),
        width: 60,
        renderCell: (params) => (
          <Stack
            spacing={1}
            direction="row"
            sx={{ alignItems: "center", marginTop: "5px" }}
          >
            <img
              src={details}
              width={20}
              height={20}
              style={{
                cursor: "pointer",
              }}
              onClick={() => {
                getDrawerData(params.row);
              }}
              alt="View Details"
            />
          </Stack>
        ),
      };
      setColumns([viewOnlyColumn, ...baseColumns]);
    }
  }, [permissionData, permissionLoading, rows.length, t]);

  // Switch change handler
  const handleSwitchChange = useCallback((
    event: React.ChangeEvent<HTMLInputElement>,
    value: UserData
  ) => {
    const collectData = {
      useR_ID: value.id,
      ranK_ID: value.ranK_ID,
      suR_NAME: value.suR_NAME,
      firsT_NAME: value.firsT_NAME,
      middlE_NAME: value.middlE_NAME,
      shorT_NAME: "",
      useR_CODE: value.useR_CODE,
      dob: value.dob,
      doa: "1900-01-01",
      doj: "1900-01-01",
      gendeR_ID: value.gendeR_ID,
      cuR_PHONE: value.cuR_PHONE,
      cuR_MOBILE: value.cuR_MOBILE,
      email: value.email,
      iS_ACTIVE: event.target.checked,
      iS_DELETED: false,
      useR_TYPE_ID: value.useR_TYPE_ID,
      otp: "",
      logiN_NAME: value.logiN_NAME,
      password: value.password,
      rolename: value.rolename,
    };

    axios
      .post(HOST_URL.HOST_URL + `USER/AddUpdateUSER`, collectData)
      .then((res) => {
        if (res.data.isSuccess) {
          getList();
          toast.success("User status updated successfully");
        } else {
          toast.error("Failed to update user status");
        }
      })
      .catch((error) => {
        console.error("Error updating user status:", error);
        toast.error("Error updating user status");
      });
  }, [getList]);

  // Delete handlers
  const handleDeleteAccept = useCallback(async (id: string) => {
    try {
      const collectData = {
        useR_ID: id,
      };

      const response = await axios.post(
        HOST_URL.HOST_URL + `USER/DeleteUSER`,
        collectData
      );

      if (response.data.isSuccess) {
        toast.success(response.data.mesg);
        getList();
      } else {
        toast.error(response.data.mesg);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete user");
    } finally {
      setDeleteId("");
    }
  }, [getList]);

  const handleDeleteReject = useCallback(() => {
    toast.warn("Deletion cancelled", { autoClose: 3000 });
    setDeleteId("");
  }, []);

  // Navigation handlers
  const routeChangeEdit = (row: UserData) => {
    navigate(`/UserManagement/UserManagementEdit`, {
      state: row,
    });
  };

  const routeChangeAdd = () => {
    navigate(`/UserManagement/UserManagementAdd`);
  };

  // Drawer data handler
  const getDrawerData = useCallback((row: UserData) => {
    setDrawerData([row]);
    setDrawerOpenUser(prev => !prev);
  }, []);

  // Modal data fetch (kept original functionality)
  const getModalList = useCallback(async (id: any) => {
    setIsReportLoading(true);
    const collectData = {
      advertisementEntryID: id,
      user_ID: -1,
      show: false,
      exportOption: ".pdf",
    };

    try {
      const response = await axios.post(
        HOST_URL.HOST_URL + `RenewAdvertisementEntry/GetRenewAdvertisementEntry`,
        collectData
      );
      setPdfReport(response.data);
    } catch (error) {
      console.error("Error fetching modal data:", error);
      toast.error("Failed to load report");
    } finally {
      setIsReportLoading(false);
    }
  }, []);

  // Loading state handling
  if (permissionLoading) {
    return (
      <Grid item lg={6} sm={6} xs={12} sx={{ marginTop: "3vh" }}>
        <Card
          style={{
            width: "100%",
            height: "50%",
            backgroundColor: "#E9FDEE",
            border: ".5px solid green ",
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </Card>
      </Grid>
    );
  }

  return (
    <>
      <Grid item lg={6} sm={6} xs={12} sx={{ marginTop: "3vh" }}>
        <Card
          style={{
            width: "100%",
            height: "50%",
            backgroundColor: "#E9FDEE",
            border: ".5px solid green ",
          }}
        >
          <Paper
            sx={{
              width: "100%",
              overflow: "hidden",
              "& .MuiDataGrid-colCell": {
                backgroundColor: `var(--grid-headerBackground)`,
                color: `var(--grid-headerColor)`,
                fontSize: 17,
                fontWeight: 900,
              },
            }}
            style={{ padding: "10px" }}
          >
            <ConfirmDialog />
            <Typography
              gutterBottom
              variant="h5"
              component="div"
              sx={{ padding: "20px" }}
              align="left"
            >
              {t("text.UserManegment")}
            </Typography>
            <Divider />

            <Box height={10} />

            {/* PDF Report Modal */}
            <Grid>
              <Modal open={docOpens} onClose={handleOtherDocClose}>
                <Box sx={style}>
                  {isReportLoading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CircularProgress />
                    </div>
                  ) : pdfReport !== null ? (
                    <embed
                      src={`data:application/pdf;base64,${pdfReport}`}
                      type="application/pdf"
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                      }}
                    />
                  ) : (
                    <Typography>{t("text.NoDataAvailable")}</Typography>
                  )}
                </Box>
              </Modal>
            </Grid>

            {/* Action Buttons - Only show if user has add permission */}
            {permissionData?.isAdd && (
              <Stack direction="row" spacing={2} classes="my-2 mb-2">
                <Button
                  onClick={routeChangeAdd}
                  variant="contained"
                  endIcon={<AddCircleIcon />}
                  size="large"
                  style={{ backgroundColor: `var(--header-background)` }}
                >
                  {t("text.add")}
                </Button>
              </Stack>
            )}

            {/* Data Grid Section */}
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </div>
            ) : (
              <Box>
                <br></br>
                <div style={{ width: "100%", backgroundColor: `var(--grid-background)` }}>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    autoHeight
                    slots={{
                      toolbar: GridToolbar,
                    }}
                    rowSpacingType="border"
                    pagination={true}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 5 } },
                    }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                      },
                    }}
                    disableRowSelectionOnClick
                  />
                </div>
              </Box>
            )}

            {/* User Details Drawer */}
            <SwipeableTemporaryDrawer
              open={drawerOpenUser}
              onClose={() => setDrawerOpenUser(false)}
              userData={drawerData}
            />
          </Paper>
        </Card>
      </Grid>
      <ToastApp />
    </>
  );
}




















// import * as React from "react";
// import Paper from "@mui/material/Paper";
// import { useEffect, useState } from "react";
// import {
//   Box,
//   Button,
//   Divider,
//   Stack,
//   TextField,
//   Typography,
// } from "@mui/material";
// import AddCircleIcon from "@mui/icons-material/AddCircle";
// import EditIcon from "@mui/icons-material/Edit";
// import DeleteIcon from "@mui/icons-material/Delete";
// import Card from "@mui/material/Card";
// import Grid from "@mui/material/Grid";
// import PrintIcon from "@mui/icons-material/Print";
// import { useNavigate, useLocation } from "react-router-dom";
// import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
// import axios from "axios";
// import Chip from "@mui/material/Chip";
// import Switch from "@mui/material/Switch";
// import HOST_URL from "../../../utils/Url";
// import moment from "moment";
// import CircularProgress from "@mui/material/CircularProgress";
// import { useTranslation } from "react-i18next";
// import VisibilityIcon from "@mui/icons-material/Visibility";
// import SwipeableTemporaryDrawer from "./SwipeableTemporaryDrawer";
// import details from "../../../assets/images/face-recognition.png";
// import reports from "../../../assets/images/report.png";
// import Modal from "@mui/material/Modal";
// import {
//   DataGrid,
//   GridColDef,
//   GridToolbar,
//   GridActionsCellItem,
// } from "@mui/x-data-grid";

// import { toast } from "react-toastify";
// import ToastApp from "../../../ToastApp";
// import { usePermissionData } from "../../../usePermissionData";
// import dayjs from "dayjs";

// const style = {
//   position: "absolute" as "absolute",
//   top: "50%",
//   left: "50%",
//   transform: "translate(-50%, -50%)",
//   width: "180vh",
//   height: "85vh",
//   bgcolor: "#f5f5f5",
//   border: "1px solid #000",
//   boxShadow: 24,
//   p: 4,
//   borderRadius: 10,
// };

// interface MenuPermission {
//   isAdd: boolean;
//   isEdit: boolean;
//   isPrint: boolean;
//   isDel: boolean;
// }

// export default function UserManagement() {
//   const [rows, setRows] = useState<any>([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [drawerOpenUser, setDrawerOpenUser] = useState(false);
//   const [drawerData, setDrawerData] = useState<any>([]);
//   const [pdfReport, setPdfReport] = useState<string | null>(null);
//   const [isReportLoading, setIsReportLoading] = useState(true);
//   const [docOpens, setDocOpens] = useState(false);

//   const [permissionData, setPermissionData] = useState<MenuPermission>({
//     isAdd: false,
//     isEdit: false,
//     isPrint: false,
//     isDel: false,
//   });

//   let navigate = useNavigate();
//   const { t } = useTranslation();

//   const location = useLocation();
//   const [columns, setColumns] = useState<any>([]);

//   const handleOtherDocClose = () => {
//     setDocOpens(false);
//   };

//   const handleOtherDocOpen = () => {
//     setDocOpens(true);
//   };

//   useEffect(() => {
//     // const dataString = localStorage.getItem("userdata");
//     //   if (dataString) {
//     //     const data = JSON.parse(dataString);
//     //     if (data && data.length > 0) {
//     //       const userPermissionData = data[0]?.userPermission;
//     //       if (userPermissionData && userPermissionData.length > 0) {
//     //         const menudata = userPermissionData[0]?.parentMenu;
//     //         for (let index = 0; index < menudata.length; index++) {
//     //           const childMenudata = menudata[index]?.childMenu;
//     //           const pathrow = childMenudata.find(
//     //             (x: any) => x.path === location.pathname
//     //           );
//     //           console.log("data", pathrow);
//     //           if (pathrow) {
//     //             setPermissionData(pathrow);
//     //             getList();
//     //           }
//     //         }
//     //       }
//     //     }
//     //   }
//     getList();
//   }, []);

//   // }, [isLoading, drawerOpenUser,drawerData]);

//   const handleSwitchChange = (
//     event: React.ChangeEvent<HTMLInputElement>,
//     value: any
//   ) => {
//     console.log(value);
//     console.log("switch", event.target.checked);
//     const collectData = {
//       useR_ID: value.id,
//       ranK_ID: value.ranK_ID,
//       suR_NAME: value.suR_NAME,
//       firsT_NAME: value.firsT_NAME,
//       middlE_NAME: value.middlE_NAME,
//       shorT_NAME: "",
//       useR_CODE: value.useR_CODE,
//       dob: value.dob,
//       doa: "1900-01-01",
//       doj: "1900-01-01",
//       gendeR_ID: value.gendeR_ID,
//       cuR_PHONE: value.cuR_PHONE,
//       cuR_MOBILE: value.cuR_MOBILE,
//       email: value.email,
//       iS_ACTIVE: event.target.checked,
//       iS_DELETED: false,
//       useR_TYPE_ID: value.useR_TYPE_ID,
//       otp: "",
//       logiN_NAME: value.logiN_NAME,
//       password: value.password,
//       rolename: value.rolename,
//     };
//     console.log("value check", collectData);
//     axios
//       .post(HOST_URL.HOST_URL + `USER/AddUpdateUSER`, collectData)
//       .then((res) => {
//         if (res.data.isSuccess) {
//           getList();
//         }
//       });
//   };

//   let delete_id = "";
//   const accept = () => {
//     const collectData = {
//       useR_ID: delete_id,
//     };
//     console.log("collectData " + JSON.stringify(collectData));
//     axios
//       .post(HOST_URL.HOST_URL + `USER/DeleteUSER`, collectData)
//       .then((response) => {
//         if (response.data.isSuccess) {
//           toast.success(response.data.mesg);
//         } else {
//           toast.error(response.data.mesg);
//         }
//         getList();
//       });
//   };
//   const reject = () => {
//     // toast.warn({summary: 'Rejected', detail: 'You have rejected', life: 3000 });
//     toast.warn("Rejected: You have rejected", { autoClose: 3000 });
//   };

//   const handledeleteClick = (del_id: any) => {
//     // console.log(del_id + " del_id ");
//     delete_id = del_id;
//     confirmDialog({
//       message: "Do you want to delete this record ?",
//       header: "Delete Confirmation",
//       icon: "pi pi-info-circle",
//       acceptClassName: "p=-button-danger",
//       accept,
//       reject,
//     });
//   };

//   const getModalList = async (id: any) => {
//     setIsReportLoading(true);
//     const collectData = {
//       advertisementEntryID: id,
//       user_ID: -1,
//       show: false,
//       exportOption: ".pdf",
//     };
//     console.log("collectData", collectData);

//     try {
//       const response = await axios.post(
//         HOST_URL.HOST_URL +
//         `RenewAdvertisementEntry/GetRenewAdvertisementEntry`,
//         collectData
//       );

//       setPdfReport(response.data);
//     } catch (error) {
//       console.error("Error fetching modal data:", error);
//     } finally {
//       setIsReportLoading(false);
//     }
//   };

//   const getList = () => {
//     const collectData = {
//       useR_ID: "-1",
//     };
//     try {
//       axios
//         .post(HOST_URL.HOST_URL + `USER/GetUSER`, collectData)
//         .then((res) => {
//           // console.log("result" + JSON.stringify(res.data.data));
//           const data = res.data.data;
//           const arr = data.map((item: any, index: any) => ({
//             ...item,
//             serialNo: index + 1,
//             id: item.useR_ID,
//             name:
//               item.firsT_NAME +
//               " " +
//               (item.middlE_NAME == " " ? item.middlE_NAME + " " : "") +
//               "" +
//               item.suR_NAME,
//             dob: dayjs(item.dob).format("YYYY-MM-DD"),
//           }));
//           setRows(arr);
//           setIsLoading(false);

//           if (data.length > 0) {
//             const columns: GridColDef[] = [
//               {
//                 field: "actions",
//                 headerClassName: "MuiDataGrid-colCell",
//                 headerName: t("text.Action"),
//                 width: 160,

//                 renderCell: (params) => {
//                   // console.log("Is Edit Allowed:", permissionData?.isEdit);
//                   return [
//                     <Stack
//                       spacing={1}
//                       direction="row"
//                       sx={{ alignItems: "center", marginTop: "5px" }}
//                     >
//                       {/* {permissionData?.isEdit ? ( */}
//                       <EditIcon
//                         style={{
//                           fontSize: "20px",
//                           color: "blue",
//                           cursor: "pointer",
//                         }}
//                         className="cursor-pointer"
//                         onClick={() => routeChangeEdit(params.row)}
//                       />
//                       {/* ) : (
//                         ""
//                       )} */}
//                       <img
//                         src={details}
//                         width={20}
//                         height={20}
//                         style={{
//                           cursor: "pointer",
//                         }}
//                         onClick={() => {
//                           getDrawerData(params.row);
//                         }}
//                       />{" "}
//                       <SwipeableTemporaryDrawer
//                         open={drawerOpenUser}
//                         // onClose={closeDrawer}
//                         onClose={() => setDrawerOpenUser(!drawerOpenUser)}
//                         userData={drawerData}
//                       />
//                       {/* {permissionData?.isDel ? ( */}
//                       <DeleteIcon
//                         style={{
//                           fontSize: "20px",
//                           color: "red",
//                           cursor: "pointer",
//                         }}
//                         onClick={() => {
//                           handledeleteClick(params.row.id);
//                         }}
//                       />
//                       {/* ) : (
//                         ""
//                       )} */}
//                       <Switch
//                         checked={Boolean(params.row.iS_ACTIVE)}
//                         style={{
//                           color: params.row.iS_ACTIVE ? "green" : "#FE0000",
//                         }}
//                         onChange={(value: any) =>
//                           handleSwitchChange(value, params.row)
//                         }
//                         inputProps={{
//                           "aria-label": "Toggle Switch",
//                         }}
//                       />
//                     </Stack>,
//                   ];
//                 },
//               },

//               {
//                 field: "serialNo",
//                 headerName: t("text.SrNo"),
//                 flex: 1,
//                 headerClassName: "MuiDataGrid-colCell",
//                 minWidth: 60
//               },
//               {
//                 field: "name",
//                 headerName: t("text.Name"),
//                 flex: 1,
//                 headerClassName: "MuiDataGrid-colCell",
//                 minWidth: 150
//               },
//               {
//                 field: "logiN_NAME",
//                 headerName: t("text.LoginName"),
//                 flex: 1,
//                 headerClassName: "MuiDataGrid-colCell",
//                 minWidth: 150
//               },
//               // {
//               //   field: "cuR_MOBILE",
//               //   headerName: t("text.MobileNo"),
//               //   flex: 1,
//               //   headerClassName: "MuiDataGrid-colCell",
//               // },
//               {
//                 field: "email",
//                 headerName: t("text.Email"),
//                 flex: 1,
//                 headerClassName: "MuiDataGrid-colCell",
//                 minWidth: 200
//               },
//               {
//                 field: "dob",
//                 headerName: t("text.DOB"),
//                 flex: 1,
//                 headerClassName: "MuiDataGrid-colCell",
//                 minWidth: 120
//               },
//               {
//                 field: "rolename",
//                 headerName: t("text.Role"),
//                 flex: 1,
//                 headerClassName: "MuiDataGrid-colCell",
//                 minWidth: 120
//               },

//               {
//                 field: "isActive",
//                 headerName: t("text.Status"),
//                 flex: 1,
//                 headerClassName: "MuiDataGrid-colCell",
//                 renderCell: (params) => [
//                   <Stack direction="row" spacing={1}>
//                     {params.row.iS_ACTIVE === true ? (
//                       <Chip
//                         label={t("text.Active")}
//                         color="success"
//                         style={{ fontSize: "14px" }}
//                       />
//                     ) : (
//                       <Chip
//                         // label="InActive"
//                         label={t("text.InActive")}
//                         color="error"
//                         style={{ fontSize: "14px" }}
//                       />
//                     )}
//                   </Stack>,
//                 ],
//                 minWidth: 100
//               },
//             ];
//             setColumns(columns as any);
//           }
//         });
//     } catch (error) {
//       console.error("Error fetching data:", error);
//       // setIsLoading(false);
//     }
//   };

//   const routeChangeEdit = (row: any) => {
//     // console.log(row);
//     let path = `/UserManagement/UserManagementEdit`;
//     navigate(path, {
//       state: row,
//     });
//   };

//   const routeChangeAdd = () => {
//     let path = `/UserManagement/UserManagementAdd`;
//     navigate(path);
//   };

//   const getDrawerData = (row: any) => {
//     console.log(row);
//     setDrawerData([row]);
//     console.log(drawerData);

//     if (drawerOpenUser) {
//       setDrawerOpenUser(false);
//     } else {
//       setDrawerOpenUser(true);
//     }

//     // const collectData = {
//     //   useR_ID: id,
//     // };

//     // await axios
//     //   .post(HOST_URL.HOST_URL + `USER/GetUSER`, collectData)
//     //   .then((res) => {
//     //     console.log("result Drawer" + JSON.stringify(res.data.data));
//     //     setDrawerData(res.data.data);
//     //     setDrawerOpen(!drawerOpen);
//     //   });
//   };

//   return (
//     <>
//       <div></div>
//       <Grid item lg={6} sm={6} xs={12} sx={{ marginTop: "3vh" }}>
//         <Card
//           style={{
//             width: "100%",
//             height: "50%",
//             backgroundColor: "#E9FDEE",
//             border: ".5px solid green ",
//           }}
//         >
//           <Paper
//             sx={{
//               width: "100%",
//               overflow: "hidden",
//               "& .MuiDataGrid-colCell": {
//                 backgroundColor: `var(--grid-headerBackground)`,
//                 color: `var(--grid-headerColor)`,
//                 fontSize: 17,
//                 fontWeight: 900,
//               },
//             }}
//             style={{ padding: "10px" }}
//           >
//             <ConfirmDialog />
//             <Typography
//               gutterBottom
//               variant="h5"
//               component="div"
//               sx={{ padding: "20px" }}
//               align="left"
//             >
//               {t("text.UserManegment")}
//             </Typography>
//             <Divider />

//             <Box height={10} />

//             <Grid>
//               <Modal open={docOpens} onClose={handleOtherDocClose}>
//                 <Box sx={style}>
//                   {isReportLoading ? (
//                     <div
//                       style={{
//                         display: "flex",
//                         justifyContent: "center",
//                         alignItems: "center",
//                       }}
//                     >
//                       <CircularProgress />
//                     </div>
//                   ) : pdfReport !== null ? (
//                     <embed
//                       src={`data:application/pdf;base64,${pdfReport}`}
//                       type="application/pdf"
//                       style={{
//                         width: "100%",
//                         height: "100%",
//                         border: "none",
//                       }}
//                     />
//                   ) : (
//                     <Typography>{t("text.NoDataAvailable")}</Typography>
//                   )}
//                 </Box>
//               </Modal>
//             </Grid>

//             <Stack direction="row" spacing={2} classes="my-2 mb-2">
//               {/* {permissionData?.isAdd == true ? ( */}
//               <Button
//                 onClick={routeChangeAdd}
//                 variant="contained"
//                 endIcon={<AddCircleIcon />}
//                 size="large"
//                 style={{ backgroundColor: `var(--header-background)` }}
//               >
//                 {t("text.add")}
//               </Button>
//               {/* ) : (
//                 ""
//               )} */}

//               {/* {permissionData?.isPrint == true ? (
//                 <Button
//                   variant="contained"
//                   endIcon={<PrintIcon />}
//                   size="large"
//                 >
//                   {t("text.print")}
//                 </Button>
//               ) : (
//                 ""
//               )} */}
//             </Stack>
//             {isLoading ? (
//               <div
//                 style={{
//                   display: "flex",
//                   justifyContent: "center",
//                   alignItems: "center",
//                 }}
//               >
//                 <CircularProgress />
//               </div>
//             ) : (
//               <Box>
//                 <br></br>
//                 <div style={{ width: "100%", backgroundColor: `var(--grid-background)` }}>
//                   <DataGrid
//                     rows={rows}
//                     columns={columns}
//                     autoHeight
//                     slots={{
//                       toolbar: GridToolbar,
//                     }}
//                     rowSpacingType="border"
//                     pagination={true}
//                     pageSizeOptions={[5, 10, 25, 50, 100].map((size) => ({
//                       value: size,
//                       label: `${size}`,
//                     }))}
//                     initialState={{
//                       pagination: { paginationModel: { pageSize: 5 } },
//                     }}
//                     slotProps={{
//                       toolbar: {
//                         showQuickFilter: true,
//                       },
//                     }}
//                   />
//                 </div>
//               </Box>
//             )}
//           </Paper>
//         </Card>
//       </Grid>
//       <ToastApp />
//     </>
//   );
// }
